import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
} from '@mui/material';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import EditNoteIcon from '@mui/icons-material/EditNote';
import LayersIcon from '@mui/icons-material/Layers';
import { apiFetch } from '../../auth/apiFetch';

const tools = [
  {
    name: 'DOCX Upload',
    description: 'Upload DOCX files and match with product metadata',
    icon: CloudUploadIcon,
    path: '/docx-upload',
    color: '#0891b2',
  },
  {
    name: 'Chunk Tool',
    description: 'Split PDF documents into individual chunks with metadata assignment',
    icon: ContentCutIcon,
    path: '/chunks',
    color: '#35677f',
  },
  {
    name: 'Image Counter',
    description: 'Count and analyze images in PDF documents',
    icon: ImageSearchIcon,
    path: '/count-images',
    color: '#fbba00',
  },
  {
    name: 'A11y Checker',
    description: 'Analyze PDF documents with veraPDF',
    icon: AccessibilityNewIcon,
    path: '/a11y-check',
    color: '#fbba00',
    disabled: true,
  },
  {
    name: 'XMP Ingestion',
    description: 'Inject metadata (creator, title, description, subjects, rights) into PDF XMP streams',
    icon: EditNoteIcon,
    path: '/edit-pdf',
    color: '#16a34a',
  },
  {
    name: 'Add Cover',
    description: 'Add cover images from S3 into PDFs as a PDF/UA-1 compliant first page',
    icon: LayersIcon,
    path: '/add-cover',
    color: '#7c3aed',
  },
];

function ToolCard({ tool }) {
  const navigate = useNavigate();
  const IconComponent = tool.icon;
  const isDisabled = tool.disabled;

  return (
    <Paper
      elevation={isDisabled ? 0 : 2}
      sx={{
        p: 3,
        height: '100%',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease-in-out',
        opacity: isDisabled ? 0.5 : 1,
        ...(!isDisabled && {
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          },
        }),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}
      onClick={() => !isDisabled && navigate(tool.path)}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          backgroundColor: isDisabled ? 'grey.200' : `${tool.color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        }}
      >
        <IconComponent sx={{ fontSize: 36, color: isDisabled ? 'grey.400' : tool.color }} />
      </Box>
      <Typography variant="h6" fontWeight={600} gutterBottom color={isDisabled ? 'text.disabled' : 'text.primary'}>
        {tool.name}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {tool.description}
      </Typography>
    </Paper>
  );
}

function HomePage() {
  const [lastImport, setLastImport] = useState(null);
  const apiBaseUrl = process.env.REACT_APP_API_URL || window.location.origin;

  useEffect(() => {
    apiFetch(`${apiBaseUrl}/api/last-import-status`)
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setLastImport(data); })
      .catch(() => {});
  }, [apiBaseUrl]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" fontWeight={600} gutterBottom sx={{ mb: 1 }}>
        Production Tools
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: lastImport ? 1 : 4 }}>
        Select a tool to get started
      </Typography>

      {lastImport && (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 4 }}>
          Metadata last imported: {new Date(lastImport.completed_at.endsWith('Z') ? lastImport.completed_at : lastImport.completed_at + 'Z').toLocaleString()}
        </Typography>
      )}

      <Grid container spacing={3}>
        {tools.map((tool) => (
          <Grid item xs={12} sm={6} md={4} key={tool.path}>
            <ToolCard tool={tool} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default HomePage;
